// Moldura da aplicação logada: topo com a marca do grupo e o usuário, menu lateral (vira
// gaveta no celular) e a área de conteúdo.

import { Bell, Building2, ChartLine, History, LayoutDashboard, ListOrdered, LogOut, Menu, Palette, PlugZap, RadioTower, Sparkles, Stethoscope, User, Users, Waves, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { NavLink, Outlet } from "react-router";
import { ROLE_LABEL } from "../lib/api";
import { useLiveUpdates } from "../lib/live";
import { BrandMark, canManageAnything, isAdmin, managedGroupIds, useApplyBranding, useLogout, useMe } from "../lib/session";
import { PlatformAlertBanner } from "./PlatformAlerts";
import { cx } from "./ui";

interface Item {
  to: string;
  label: string;
  icon: ReactNode;
}

export function Shell() {
  const { data: me } = useMe();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  useApplyBranding(me?.branding);
  const live = useLiveUpdates();

  const main: Item[] = [
    { to: "/", label: "Visão geral", icon: <LayoutDashboard className="size-4" /> },
    { to: "/alarmes", label: "Alarmes", icon: <Bell className="size-4" /> },
    { to: "/graficos", label: "Gráficos", icon: <ChartLine className="size-4" /> },
    { to: "/oscilografias", label: "Oscilografias", icon: <Waves className="size-4" /> },
    { to: "/eventos", label: "Sequência de eventos", icon: <ListOrdered className="size-4" /> },
    { to: "/analises", label: "Análises com IA", icon: <Sparkles className="size-4" /> },
  ];
  const admin: Item[] = [];
  if (canManageAnything(me)) {
    admin.push({ to: "/admin/hierarquia", label: "Usinas e reguladores", icon: <Building2 className="size-4" /> });
    admin.push({ to: "/admin/usuarios", label: "Usuários", icon: <Users className="size-4" /> });
  }
  if (isAdmin(me) || managedGroupIds(me).length) {
    admin.push({ to: "/admin/marca", label: "Identidade visual", icon: <Palette className="size-4" /> });
    admin.push({ to: "/admin/auditoria", label: "Auditoria", icon: <History className="size-4" /> });
  }
  if (isAdmin(me)) {
    admin.push({ to: "/admin/gateways", label: "Gateways", icon: <RadioTower className="size-4" /> });
    admin.push({ to: "/admin/diagnostico", label: "Diagnóstico", icon: <Stethoscope className="size-4" /> });
    admin.push({ to: "/admin/fontes", label: "Fontes sem cadastro", icon: <PlugZap className="size-4" /> });
  }

  const role = me?.memberships[0]?.role;
  const nav = (
    <nav className="space-y-6 p-3" onClick={() => setOpen(false)}>
      <Section items={main} />
      {admin.length > 0 && <Section title="Administração" items={admin} />}
    </nav>
  );

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-line bg-surface/95 px-4 backdrop-blur">
        <button type="button" className="rounded p-1.5 hover:bg-surface-2 md:hidden" onClick={() => setOpen(true)} aria-label="Abrir menu">
          <Menu className="size-5" />
        </button>
        <BrandMark branding={me?.branding} />
        <div className="ml-auto flex items-center gap-1">
          <span
            className="mr-2 hidden items-center gap-1.5 text-xs text-muted sm:flex"
            title={live ? "Recebendo atualizações em tempo real" : "Sem conexão de tempo real; atualizando periodicamente"}
          >
            <span className={cx("size-2 rounded-full", live ? "bg-ok" : "bg-muted")} aria-hidden />
            {live ? "tempo real" : "reconectando"}
          </span>
          <NavLink
            to="/perfil"
            className={({ isActive }) =>
              cx("flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-surface-2", isActive && "bg-surface-2")
            }
          >
            <User className="size-4 text-muted" aria-hidden />
            <span className="hidden text-left sm:block">
              <span className="block leading-tight">{me?.user.name}</span>
              {role && <span className="block text-xs leading-tight text-muted">{ROLE_LABEL[role]}</span>}
            </span>
          </NavLink>
          <button type="button" onClick={logout} className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg" title="Sair" aria-label="Sair">
            <LogOut className="size-4" />
          </button>
        </div>
      </header>
      <PlatformAlertBanner enabled={isAdmin(me)} />

      <div className="flex">
        <aside className="sticky top-14 hidden h-[calc(100vh-3.5rem)] w-60 shrink-0 overflow-y-auto border-r border-line bg-surface md:block">
          {nav}
        </aside>

        {open && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
            <aside className="absolute inset-y-0 left-0 w-72 overflow-y-auto bg-surface shadow-xl">
              <div className="flex h-14 items-center justify-between border-b border-line px-4">
                <BrandMark branding={me?.branding} />
                <button type="button" onClick={() => setOpen(false)} className="rounded p-1.5 hover:bg-surface-2" aria-label="Fechar menu">
                  <X className="size-5" />
                </button>
              </div>
              {nav}
            </aside>
          </div>
        )}

        <main className="min-w-0 flex-1 px-4 py-6 md:px-8">
          <div className="mx-auto max-w-6xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function Section({ title, items }: { title?: string; items: Item[] }) {
  return (
    <div>
      {title && <p className="mb-1 px-3 text-xs font-medium uppercase tracking-wide text-muted">{title}</p>}
      <ul className="space-y-0.5">
        {items.map((i) => (
          <li key={i.to}>
            <NavLink
              to={i.to}
              end={i.to === "/"}
              className={({ isActive }) =>
                cx(
                  "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm",
                  isActive ? "bg-brand/10 font-medium text-brand" : "text-fg hover:bg-surface-2",
                )
              }
            >
              {i.icon}
              {i.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}
